import React from 'react';

import { LinkWithQuery } from 'components/common/LinkWithQuery';
import PlatformDetailPage, { PlatformDetailPageData } from 'components/common/PlatformDetailPage';
import {
  ANALYTICS_ROUTE,
  DATA_AND_PRIVACY_ROUTE,
  ENTERPRISE_CONTACT_LINK,
  FLOW_ROUTE,
  SEGMENTS_ROUTE,
  STUDIO_ROUTE,
} from 'components/constants';

import styles from './AI.module.scss';

const improvementStages = [
  {
    number: '01',
    eyebrow: 'First-party data',
    title: 'Capture what people choose.',
    text:
      'Turn answers, choices, form submissions and custom events into structured data tied to the product and step where each signal was created.',
    href: DATA_AND_PRIVACY_ROUTE,
    linkLabel: 'Explore data collection',
  },
  {
    number: '02',
    eyebrow: 'Journey analytics',
    title: 'Understand the whole path.',
    text:
      'See views, answers, navigation, completion and drop-off. Move from the organization view to the exact card, path or audience that needs attention.',
    href: ANALYTICS_ROUTE,
    linkLabel: 'Explore analytics',
  },
  {
    number: '03',
    eyebrow: 'Personalization',
    title: 'Use the signal while it matters.',
    text:
      'Adapt the live journey with conditional paths, scoring, dynamic fields and audience rules, then send the relevant context into the next approved system.',
    href: FLOW_ROUTE,
    linkLabel: 'Explore personalized flows',
  },
  {
    number: '04',
    eyebrow: 'A/B testing',
    title: 'Compare a deliberate change.',
    text:
      'Create controlled variations in Studio and compare how content, design or flow changes affect completion and other meaningful outcomes.',
    href: ANALYTICS_ROUTE,
    linkLabel: 'See measurement tools',
  },
  {
    number: '05',
    eyebrow: 'Continuous improvement',
    title: 'Carry the learning forward.',
    text:
      'Refine the approved product, publish the next version and measure again. Apply useful lessons across products, templates, teams and future launches.',
    href: STUDIO_ROUTE,
    linkLabel: 'Explore Storycards Studio',
  },
];

const ContinuousImprovement: React.FC = () => (
  <section className={styles.improvement} aria-labelledby="continuous-improvement-title">
    <div className={styles.improvementInner}>
      <header className={styles.improvementHeader}>
        <div>
          <p className={styles.eyebrow}>After launch</p>
          <h2 id="continuous-improvement-title">Publishing starts the next operating loop.</h2>
        </div>
        <p>
          A Storycards product keeps producing useful signals after it goes live. Teams can understand behavior,
          personalize what happens next, test a focused change and improve the product without rebuilding the system
          around it.
        </p>
      </header>

      <figure className={styles.videoPlaceholder} aria-labelledby="improvement-video-caption">
        <div className={styles.videoToolbar} aria-hidden="true">
          <span>Storycards continuous improvement</span>
          <span>Video in production</span>
        </div>
        <div className={styles.videoPlaceholderBody}>
          <span className={styles.videoPlay} aria-hidden="true"><i /></span>
          <strong>Video coming here</strong>
          <p>Create. Learn. Personalize. Test. Improve.</p>
        </div>
        <figcaption id="improvement-video-caption">
          Reserved for the Storycards product video showing the path from live interaction data to the next approved
          version.
        </figcaption>
      </figure>

      <div className={styles.loopIntroduction}>
        <p className={styles.eyebrow}>One connected loop</p>
        <h3>Every interaction can inform the current journey and the next version.</h3>
        <p>
          The same operating model connects first-party signals, product analytics, real-time personalization and
          controlled experimentation. AI can help teams analyze and create, while authorized people decide what changes
          and when it is published.
        </p>
      </div>

      <ol className={styles.improvementStages}>
        {improvementStages.map((stage) => (
          <li key={stage.number}>
            <div className={styles.stageIndex}>
              <span>{stage.number}</span>
              <small>{stage.eyebrow}</small>
            </div>
            <div className={styles.stageCopy}>
              <h3>{stage.title}</h3>
              <p>{stage.text}</p>
              <LinkWithQuery to={stage.href}>
                {stage.linkLabel} <span aria-hidden="true">→</span>
              </LinkWithQuery>
            </div>
          </li>
        ))}
      </ol>

      <div className={styles.signalSystem}>
        <div className={styles.signalSystemIntro}>
          <p className={styles.eyebrow}>One signal, three useful actions</p>
          <h3>Keep audience context connected instead of leaving it inside one campaign.</h3>
        </div>
        <div className={styles.signalActions}>
          <article>
            <span>Now</span>
            <h4>Personalize the live journey</h4>
            <p>Use an answer, score or known field to select the relevant path, result or next action.</p>
          </article>
          <article>
            <span>Next</span>
            <h4>Continue in your data stack</h4>
            <p>Send selected records and events to analytics, CRM, databases and internal systems.</p>
          </article>
          <article>
            <span>Over time</span>
            <h4>Improve the product portfolio</h4>
            <p>Use completion, drop-off and experiment results to guide the next version and future launches.</p>
          </article>
        </div>
      </div>
    </div>
  </section>
);

const data: PlatformDetailPageData = {
  eyebrow: 'Storycards AI',
  title: 'Turn a brief into a governed, editable product.',
  subtitle:
    'Start with a prompt, URL or data brief. Storycards applies your brand, approved components and permissions, then creates a product your team can refine in Studio.',
  panelLabel: 'Organization-aware generation',
  panelTitle: 'Flexible prompts. Governed output.',
  panelItems: ['Your organization context', 'Approved Storycards components', 'Brand, security and permission rules'],
  heroExperience: {
    prompt: 'Build an 8-question mortgage advisor for first-time buyers.',
    response: 'Creating the journey with your approved brand, components and publishing rules.',
    context: ['Brand DNA', 'Approved components', 'Permissions'],
    productEyebrow: 'Mortgage advisor',
    productTitle: 'Find the right mortgage for you.',
    productText: 'Answer 8 questions to get a personalized recommendation.',
  },
  statementTitle: 'AI speed, without losing the system behind the work.',
  statementText:
    'Storycards creates with the context your organization has already defined: brand, language, approved components, security settings and user permissions. The result is a real Storycards product, ready for visual editing, review, publishing and improvement from live performance data.',
  capabilitiesTitle: 'Everything needed between the brief and the live product.',
  capabilitiesIntro:
    'Move quickly from an idea to an interactive product while keeping the design system, team workflow and publishing controls intact.',
  capabilities: [
    {
      title: 'Organization-aware creation',
      text: 'Brand DNA, language, design rules and platform configuration become persistent creation context.',
    },
    {
      title: 'Live, editable canvas',
      text: 'Every instruction appears on the canvas and remains open to visual editing at every breakpoint in Studio.',
    },
    {
      title: 'Permission-aware collaboration',
      text: 'Teams can continue one another’s work while roles control who can create, edit, approve and publish.',
    },
    {
      title: 'Governed, production-ready products',
      text: 'AI assembles approved Storycards components instead of injecting uncontrolled code into the experience.',
    },
  ],
  workflowTitle: 'One continuous path from request to launch.',
  workflowIntro:
    'AI accelerates creation. Storycards keeps the work editable, shared and ready for an authorized team to publish and operate.',
  workflow: [
    { title: 'Brief', text: 'Start with a prompt, URL or data brief and describe the audience, goal and desired outcome.' },
    { title: 'Assemble', text: 'Storycards combines the request with organizational context and approved components.' },
    { title: 'Perfect', text: 'Watch changes appear live, then move and refine every element in the visual Studio.' },
    { title: 'Publish', text: 'Review the complete product and publish through the roles and controls defined by your organization.' },
  ],
  outcomesTitle: 'Each launch makes the next product decision better.',
  outcomes: [
    {
      title: 'A clearer view of audience intent',
      text: 'Answers, choices and actions become structured first-party signals rather than anonymous traffic alone.',
    },
    {
      title: 'More relevant journeys and follow-up',
      text: 'Use audience context to personalize the experience, create segments and continue into approved business systems.',
    },
    {
      title: 'An evidence-based improvement process',
      text: 'Use completion, drop-off and experiment results to decide what to refine instead of guessing after launch.',
    },
  ],
  compactOutcomes: true,
  faqTitle: 'How governed AI continues after launch.',
  faqIntro:
    'The product remains editable, measurable and connected after it is published. These are the operating questions enterprise teams ask most often.',
  faqs: [
    {
      question: 'What first-party data can a Storycards product collect?',
      answer:
        'Storycards can capture answers, choices, form submissions, scores, navigation and custom events as structured data. Each signal can retain the product and step context needed for analysis, personalization or approved follow-up.',
    },
    {
      question: 'How does Storycards personalize a live journey?',
      answer:
        'Teams can use conditional paths, scoring, dynamic fields, URL parameters and audience rules to adapt content, results and next actions. The same signals can also create segments or continue into CRM, analytics and internal systems.',
    },
    {
      question: 'How do analytics and A/B testing improve the product?',
      answer:
        'Teams can review views, answers, completion and drop-off, then create controlled variations in Studio. A/B testing helps compare how a change to content, design or flow affects the outcomes the team selected.',
    },
    {
      question: 'Does Storycards AI change or publish a live product automatically?',
      answer:
        'AI can assist creation and analysis, but the product stays inside the organization’s editing, review and publishing workflow. Authorized teams decide which changes to make and when an approved version goes live.',
    },
    {
      question: 'Can Storycards data remain connected to our existing systems?',
      answer:
        'Yes. Selected records and interaction events can be sent to analytics, CRM, databases and internal systems through integrations, APIs and webhooks, so Storycards does not need to become a separate data silo.',
    },
  ],
  relatedTitle: 'Explore the system behind continuous improvement.',
  relatedLinks: [
    {
      label: 'First-party data collection',
      href: DATA_AND_PRIVACY_ROUTE,
      text: 'See how answers, choices and submissions become structured data your organization controls.',
    },
    {
      label: 'Journey analytics',
      href: ANALYTICS_ROUTE,
      text: 'Measure paths, answers, completion and drop-off across every interactive product.',
    },
    {
      label: 'Audience segments',
      href: SEGMENTS_ROUTE,
      text: 'Turn explicit choices and behavior into useful audience context and approved next actions.',
    },
  ],
  ctaTitle: 'Create the first version. Keep improving every version after it.',
  ctaText: 'Build a branded, governed product that learns from real interaction data and remains fully editable in Studio.',
  secondaryCta: { label: 'Talk to our enterprise team', href: ENTERPRISE_CONTACT_LINK },
};

const AI: React.FC = () => (
  <PlatformDetailPage data={data} afterWorkflow={<ContinuousImprovement />} appearance="immersive" />
);

export default AI;
